import React from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Tooltip,
  styled,
  Typography,
  useTheme,
  useMediaQuery,
  Skeleton,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { GraduationCap, MenuIcon } from "lucide-react";
import UserMenu from "./UserMenu";
import { useDrawer } from "../../services/providers/DrawerContext";
import NavigationSearch from "../NavigationSearch";
import { trackEvent } from "../../lib/mixpanel";
import AcademicYearSelector from "../AcademicYearSelector";
import { NotificationBell } from "../Notifications/NotificationBell";
import { useAuthStore } from "../../store/authstore";
import { useInstitute } from "../../hooks/useInstitute";

const StyledAppBar = styled(AppBar)(({ theme }) => {
  // Match NavigationSearch border: alpha(primary.main, 0.45)
  const borderColor =
    theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.16)"
      : alpha(theme.palette.primary.main, 0.45);

  return {
    position: "relative",
    width: "100%",
    margin: 0,
    borderRadius: "0 0 16px 16px",
    zIndex: theme.zIndex.drawer + 1,
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(30, 41, 59, 0.92)"
        : "#ffffff",
    backdropFilter: "blur(10px) saturate(180%)",
    WebkitBackdropFilter: "blur(10px) saturate(180%)",
    border: "none",
    backgroundImage: "none",
    "&.MuiPaper-root": {
      borderBottom: `1px solid ${borderColor}`,
      boxShadow: `0 1px 0 0 ${borderColor}`,
    },
  };
});

const IconButtonContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  "& .MuiIconButton-root": {
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    backdropFilter: "blur(8px)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.25)",
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    },
  },
}));

const Header: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { open, toggleDrawer } = useDrawer();
  const { session } = useAuthStore();
  const { getInstitute } = useInstitute(session?.user?.instituteId ?? 0);
  const { data: instituteData, isLoading: isInstituteLoading } = getInstitute();
  const institute = instituteData?.institute;
  const schoolName =
    institute?.instituteName ||
    session?.user?.instituteName ||
    "SkolarMinds";
  const logoUrl = typeof institute?.logo === "string" ? institute.logo : null;

  const handleSidebarToggle = () => {
    toggleDrawer();
    trackEvent("Sidebar Toggled", {
      platform: isMobile ? "mobile" : "desktop",
      action: open ? "close" : "open",
      source: "header",
    });
  };

  return (
    <StyledAppBar elevation={0} color="transparent">
      <Toolbar sx={{ gap: 1.5, minHeight: { xs: 64, sm: 68 }, px: { xs: 1.5, md: 2.5 } }}>
        {isMobile && (
          <Tooltip title={open ? "Close sidebar" : "Open sidebar"}>
            <IconButton
              color="inherit"
              aria-label={open ? "Close sidebar" : "Open sidebar"}
              edge="start"
              onClick={handleSidebarToggle}
              sx={{
                borderRadius: "10px",
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <MenuIcon size={20} color={theme.palette.primary.main} />
            </IconButton>
          </Tooltip>
        )}

        <Box
          sx={{
            flexShrink: 0,
            display: "inline-flex",
            alignItems: "center",
            gap: 1.25,
            px: 1.5,
            py: 0.75,
            borderRadius: "10px",
            bgcolor: "rgba(138, 112, 214, 0.1)",
            border: "1.5px solid rgba(138, 112, 214, 0.25)",
            maxWidth: { xs: "42vw", sm: "none" },
          }}
        >
          {isInstituteLoading ? (
            <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: 1, flexShrink: 0 }} />
          ) : logoUrl ? (
            <Box
              component="img"
              src={logoUrl}
              alt={schoolName}
              sx={{ width: 32, height: 32, objectFit: "contain", borderRadius: 1, flexShrink: 0, display: "block" }}
            />
          ) : (
            <GraduationCap size={28} color={theme.palette.secondary.main} style={{ flexShrink: 0 }} />
          )}
          {isInstituteLoading ? (
            <Skeleton variant="text" width={180} height={24} />
          ) : (
            <Typography
              component="div"
              title={schoolName}
              sx={{
                fontWeight: 700,
                fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" },
                lineHeight: 1.35,
                color: "text.primary",
                whiteSpace: { xs: "nowrap", md: "normal" },
                overflow: { xs: "hidden", md: "visible" },
                textOverflow: { xs: "ellipsis", md: "clip" },
              }}
            >
              {schoolName}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            justifyContent: "center",
            px: { xs: 0.5, sm: 1, md: 2 },
          }}
        >
          <Box sx={{ width: "100%", maxWidth: { xs: "100%", sm: 420, md: 560, lg: 640 } }}>
            <NavigationSearch />
          </Box>
        </Box>

        <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 1 }}>
          <AcademicYearSelector />
          <IconButtonContainer>
            <NotificationBell />
            <UserMenu />
          </IconButtonContainer>
        </Box>
      </Toolbar>
    </StyledAppBar>
  );
};

export default Header;
